import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES } from "@app/constants";
import ImageCloudService from "@app/services/http/image.cloud.service";
import OrderService from "@app/services/http/order.service";
import { openPortalDialog } from "@app/services/modal.service";
import OrderCloudHistoryModal from "@app/components/order-cloud-history-modal";
import { ImageCloud, OrderSummary } from "@app/types";
import { formatDate, formatMoneyVN, getCloudExpiry } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { BUSY_TEXT, busyOf } from "../cloud-gaming/cloud-images";
import OrderStats from "./order-stats";
import { Cell, HistoryCell, NameCell, OrdersShell, Row, StateCell, useDebounced } from "./orders-shell";

type Filter = "" | "active" | "ended";

const HEADERS = ["Image", "Gói", "Giờ chơi", "Tạo → Hạn dùng", "Tổng tiền", "Trạng thái"];

/** Trạng thái thêm phần cứng như trang Quản lý đơn hàng Cloud cũ */
const HARDWARE: Record<number, string> = {
  2: "Thêm phần cứng: Đang chờ",
  3: "Thêm phần cứng: Thành công",
  4: "Thêm phần cứng: Thất bại",
};

/** Đơn Cloud: ImageCloudService.getImageCloud("cloud_order", page) + keyword, state, startFrom, startTo */
/** Trạng thái máy dưới "Đang thuê": cùng chữ với thẻ ở trang Cloud Gaming (đang bật, đang tắt, đang chờ máy…) */
const machineState = (image: ImageCloud) => {
  if (image.is_creating) {
    const busy = busyOf(image);

    return busy ? BUSY_TEXT[busy][0] : "Đang tạo";
  }

  if (image.is_running) return "Đang chạy";

  return (image.queue_at || 0) > 0 ? "Đang chờ máy" : "Đã tắt";
};

function CloudOrders() {
  const t = useT();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [images, setImages] = useState<ImageCloud[]>([]);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [state, setState] = useState<Filter>("");
  // "Xem đơn" ở Biến động số dư mở trang này với ?type=cloud&q=<mã> để tìm sẵn đơn tương ứng
  const [params] = useSearchParams();
  const [q, setQ] = useState(() => ((params.get("type") || "pc") === "cloud" ? params.get("q") || "" : ""));
  const keyword = useDebounced(q);
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [summary, setSummary] = useState<OrderSummary>();

  const { subscribeUntilDestroy, subscribeOnce } = useObservable();

  useEffect(() => {
    subscribeOnce(OrderService.getOrderSummary("cloud"), setSummary);
  }, []);

  useEffect(() => {
    subscribeUntilDestroy(
      ImageCloudService.getImageCloud("cloud_order", page, { keyword, state, startFrom, startTo }),
      (res) => {
        setImages(res?.data || []);
        setTotalPages(res?.total_page || 0);
        setCount(res?.count || 0);
        setLoaded(true);
      }
    );
  }, [page, keyword, state, startFrom, startTo]);

  return (
    <>
      <OrderStats summary={summary} />
      <OrdersShell<Filter>
        search={{
          value: q,
          placeholder: "Tìm theo tên Image, mã, IP…",
          onChange: (v) => {
            setQ(v);
            setPage(DEFAULT_PAGE);
          },
        }}
        dates={{
          from: startFrom,
          to: startTo,
          onChange: (from, to) => {
            setStartFrom(from);
            setStartTo(to);
            setPage(DEFAULT_PAGE);
          },
        }}
        tabs={{
          value: state,
          onChange: (v) => {
            setState(v);
            setPage(DEFAULT_PAGE);
          },
          items: [
            { value: "", label: "Tất cả", count: summary?.total },
            { value: "active", label: "Đang hoạt động", count: summary?.active },
            { value: "ended", label: "Đã kết thúc", count: summary?.ended },
          ],
        }}
        headers={HEADERS}
        loading={!loaded}
        empty={!images.length}
        filtered={!!keyword || !!state || !!startFrom || !!startTo}
        page={page}
        totalPages={totalPages}
        count={count}
        onPageChange={setPage}
      >
        {images.map((image) => {
          const total = image.hours_of_use || 0;
          const used = image.total_minute_used || 0;
          const percent = total ? Math.min(100, (used / 60 / total) * 100) : 0;
          const expiry = getCloudExpiry(image);
          // quá hạn sử dụng mà cron server chưa đánh dấu has_expired cũng coi là đã kết thúc
          const ended = !!image.has_expired || !!image.is_deleted || expiry.isBefore(dayjs());

          return (
            <Row key={image._id} ended={ended}>
              <NameCell
                label={HEADERS[0]}
                name={image.name || image.cloud?.name || "—"}
                id={`ORDER_CLOUD_${image._id}`}
                ip={image.resource_cloud?.ip_remote}
              />
              <Cell label={HEADERS[1]}>
                <b className="text-fg">{image.cloud?.name || "—"}</b>
                {image.cloud?.vga && <small className="text-xs text-muted">{image.cloud.vga}</small>}
              </Cell>
              <Cell label={HEADERS[2]} className="tabular-nums">
                <b className="text-fg">
                  {Math.floor(used / 60)}h{String(used % 60).padStart(2, "0")} / {total} {t("giờ")}
                </b>
                <span className="h-1.5 w-28 overflow-hidden rounded bg-surface-2 max-[760px]:ml-auto">
                  <i className="block h-full rounded bg-brand" style={{ width: `${percent}%` }} />
                </span>
                {image.is_extended && <small className="text-xs text-muted">{t("Đã gia hạn")}</small>}
              </Cell>
              <Cell label={HEADERS[3]} className="o-d whitespace-nowrap tabular-nums">
                <span>{formatDate(image.createdAt)}</span>
                <span>{expiry.format("DD/MM/YYYY HH:mm:ss")}</span>
              </Cell>
              <Cell label={HEADERS[4]} className="tabular-nums">
                <b className="text-fg">{formatMoneyVN((image.price || 0) + (image.price_hardwares || 0))}đ</b>
                {!!image.discount && (
                  <small className="text-xs text-ok">
                    {t("Chiết khấu")} {formatMoneyVN(image.discount)}đ
                  </small>
                )}
              </Cell>
              <StateCell
                label={HEADERS[5]}
                tone={image.is_deleted ? "bad" : ended ? "muted" : "ok"}
                text={image.is_deleted ? "Đã xoá" : ended ? "Kết thúc" : "Đang thuê"}
                sub={!ended ? machineState(image) : HARDWARE[image.accepted_add_hardwares]}
              />
              <HistoryCell onClick={() => openPortalDialog(OrderCloudHistoryModal, { image_id: image._id })} />
            </Row>
          );
        })}
      </OrdersShell>
    </>
  );
}

export default CloudOrders;
